import DetailsView from '../../details/DetailsView';
import { useEffect, useState } from 'react';
import { Alert, Box, Text, TextLinkButton } from 'braid-design-system';
import JobApplicationListSection from 'src/App/JobApplicationListSection';
import { observer } from 'mobx-react';
import type { IJobApplicationStore } from '../../../stores/JobApplicationStore';
import type { IUiStore } from '../../../stores/UiStore';
import translations from './.vocab';
import { useTranslations } from '@vocab/react';
import { useDeepLinkedDetailView } from 'src/pages/CandidateManagementPage/utils/useDeepLinkedDetailView';
import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';

interface Props {
  jobApplicationStore: IJobApplicationStore;
  isMobile: boolean;
  uiStore: IUiStore;
  questionnaireId?: string;
  openBillableDialog: () => void;
}

const ListResultView = ({
  jobApplicationStore,
  uiStore,
  isMobile,
  questionnaireId,
  openBillableDialog,
}: Props) => {
  const [isReadyOnClient, setIsReadyOnClient] = useState(false);
  const [
    lastOpenedApplicationCardStateForTracking,
    setLastOpenedApplicationCardStateForTracking,
  ] = useState<boolean | undefined>(undefined);

  const { t } = useTranslations(translations);

  useEffect(() => {
    setIsReadyOnClient(true);
  }, []);

  const { language } = useAppConfig();

  const { closeDeepLinkedDetailView } = useDeepLinkedDetailView();

  if (!isReadyOnClient) {
    return null;
  }

  const onReloadJobApplications = () => {
    jobApplicationStore.loadJobApplications({
      searchText: jobApplicationStore.lastResultSearchText,
      language,
    });
  };

  if (jobApplicationStore.isError) {
    return (
      <Box width="full" data-cy="global-error" margin="xsmall">
        <Alert tone="critical">
          <Box display="flex" alignItems="flexStart" justifyContent="flexStart">
            {jobApplicationStore.lastResultSearchText === '' ? (
              <Text>
                {t('Error loading the list...', {
                  TextLinkButton: (children) => (
                    <TextLinkButton
                      key={`error-loading-${children}`}
                      onClick={onReloadJobApplications}
                    >
                      {children}
                    </TextLinkButton>
                  ),
                })}
              </Text>
            ) : (
              <Text>
                {t('Error loading the list with search text', {
                  TextLinkButton: (children) => (
                    <TextLinkButton
                      key={`error-loading-${children}`}
                      onClick={onReloadJobApplications}
                    >
                      {children}
                    </TextLinkButton>
                  ),
                  ClearTextLinkButton: (children) => (
                    <TextLinkButton
                      onClick={() => {
                        jobApplicationStore.updateSearchText('');
                        jobApplicationStore.submitSearchQuery(language);
                      }}
                      key={`error-loading-with-search${children}`}
                    >
                      {children}
                    </TextLinkButton>
                  ),
                })}
              </Text>
            )}
          </Box>
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <JobApplicationListSection
        questionnaireId={questionnaireId}
        openBillableDialog={openBillableDialog}
        onChangeApplicationCardState={
          setLastOpenedApplicationCardStateForTracking
        }
      />
      <DetailsView
        isMobile={isMobile}
        uiStore={uiStore}
        questionnaireId={questionnaireId}
        jobApplication={jobApplicationStore.shownDetailsJobApplication}
        onClose={closeDeepLinkedDetailView}
        applicationCardIsExpanded={lastOpenedApplicationCardStateForTracking}
      />
    </>
  );
};

export default observer(ListResultView);
